function isObject(v) {
  return v != null && typeof v === 'object' && !Array.isArray(v);
}

/**
 * Deep-merge two subschemas for *generation* purposes (not full JSON Schema
 * semantics): union `properties` and `required`, otherwise later wins.
 */
export function mergeTwo(a, b) {
  if (!isObject(a)) return isObject(b) ? b : a;
  if (!isObject(b)) return a;
  const out = { ...a };
  Object.keys(b).forEach((key) => {
    const av = a[key];
    const bv = b[key];
    if (key === 'properties' && isObject(av) && isObject(bv)) {
      const props = { ...av };
      Object.keys(bv).forEach((p) => {
        props[p] = isObject(av[p]) && isObject(bv[p]) ? mergeTwo(av[p], bv[p]) : bv[p];
      });
      out.properties = props;
    } else if (key === 'required' && Array.isArray(av) && Array.isArray(bv)) {
      out.required = Array.from(new Set([...av, ...bv]));
    } else {
      out[key] = bv;
    }
  });
  return out;
}

/** Fold a list of subschemas left to right (ignoring non-objects). */
export function mergeSchemas(schemas) {
  return (schemas || []).filter(isObject).reduce((acc, s) => mergeTwo(acc, s), {});
}

/**
 * Collapse allOf/anyOf/oneOf into a single effective schema for generation.
 * - allOf: merge *plain* members (members with an `if` are conditional and are
 *   handled by conditionals.js — they are dropped from the merge here).
 * - anyOf/oneOf: take the first member and merge it with the base.
 */
export function resolveComposition(schema) {
  if (!isObject(schema)) return schema;
  let effective = { ...schema };

  if (Array.isArray(effective.allOf)) {
    const { allOf, ...rest } = effective;
    const plain = allOf.filter((m) => isObject(m) && !('if' in m));
    effective = mergeSchemas([rest, ...plain]);
  }
  if (Array.isArray(effective.anyOf) && effective.anyOf.length > 0) {
    const { anyOf, ...rest } = effective;
    effective = mergeTwo(rest, anyOf[0]);
  }
  if (Array.isArray(effective.oneOf) && effective.oneOf.length > 0) {
    const { oneOf, ...rest } = effective;
    effective = mergeTwo(rest, oneOf[0]);
  }
  return effective;
}
